import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatchApp } from "../lib/redux";
import { getPathDatabaseByRole } from "../actions/auth";
import { getIsBlockedActions } from "../actions/getters";
import { useCreditActionCosts } from "./useCreditActionCosts";
import { useTranslationApp } from "../lib/i18next";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { runOrganicPublishRetryFlow } from "../utils/runOrganicPublishRetryFlow";
import {
  ALERT_ICON_TYPE_SUCCESS,
  ALERT_ICON_TYPE_WARNING,
  GOOGLE_PMAX_VIDEO_MAX_DURATION_SECONDS,
  GOOGLE_PMAX_VIDEO_MIN_DURATION_SECONDS,
  VIDEO_FORMATS,
} from "../utils/constants";
import {
  buildYoutubeOrganicUploadPayload,
  extractYoutubeUrlFromUploadResponse,
  validateGooglePmaxVideoDuration,
} from "../utils/googlePmaxYoutubeUpload";
import { isOrganicPublishFullySuccessful } from "../utils/organicPublishOutcome";
import {
  ALERT_SHARE_SUCCESS,
  FIELD_REQUIRED,
  MODAL_UPLOAD_YOUTUBE_VIDEO_DURATION_INVALID,
  READY,
} from "../i18n/keysTranslations";

const ModalGallery = lazy(() => import("../components/Modal/ModalGallery"));

const DEFAULT_FORM_VALUES = {
  account: null,
  title: "",
};

export const useUploadYoutubeVideoModal = ({
  modalOpen,
  onCloseModal,
  language,
  onCallbackSave = () => {},
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_GENERATE_SCHEDULED_CONTENT_ACTION } =
    useCreditActionCosts();
  const { userID } = dispatch(getPathDatabaseByRole());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
  });

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (!modalOpen) {
      reset(DEFAULT_FORM_VALUES);
      setSelectedVideo(null);
      setIsGalleryOpen(false);
      setIsPublishing(false);
    }
  }, [modalOpen, reset]);

  const onOpenGallery = useCallback(() => {
    setIsGalleryOpen(true);
  }, []);

  const onCloseGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  const onValidateGallerySelection = useCallback(
    async (items) => {
      const videoUrl = items?.[0]?.url;
      if (!videoUrl) {
        return false;
      }

      const isValid = await validateGooglePmaxVideoDuration({ url: videoUrl });
      if (!isValid) {
        SimpleAlert({
          title: t(MODAL_UPLOAD_YOUTUBE_VIDEO_DURATION_INVALID, {
            min: GOOGLE_PMAX_VIDEO_MIN_DURATION_SECONDS,
            max: GOOGLE_PMAX_VIDEO_MAX_DURATION_SECONDS,
          }),
          icon: ALERT_ICON_TYPE_WARNING,
        });
        return false;
      }

      return true;
    },
    [t]
  );

  const onSelectVideo = useCallback(async (items) => {
    const video = items?.[0];
    if (!video?.url) {
      return;
    }

    setSelectedVideo(video);
    setIsGalleryOpen(false);
  }, []);

  const onPublish = handleSubmit(async (formValues) => {
    if (!selectedVideo?.url) {
      return;
    }

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_SCHEDULED_CONTENT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );

    if (isBlocked) {
      return;
    }

    setIsPublishing(true);

    try {
      const uploadPayload = buildYoutubeOrganicUploadPayload({
        videoUrl: selectedVideo.url,
        language,
        account: formValues.account,
        title: formValues.title,
      });

      const retryFlowResult = await runOrganicPublishRetryFlow({
        dispatch,
        initialPayload: uploadPayload,
        t,
        buildRetryPayload: ({ failedPlatforms }) => ({
          ...uploadPayload,
          platforms: failedPlatforms,
          platformsData: {
            youtube: uploadPayload.platformsData.youtube,
          },
          isRetry: true,
        }),
      });

      const response = retryFlowResult.response;
      const isSuccess = isOrganicPublishFullySuccessful(response);

      if (!isSuccess) {
        return;
      }

      const youtubeUrl = extractYoutubeUrlFromUploadResponse(response);

      SimpleAlert({
        title: t(READY),
        text: t(ALERT_SHARE_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });

      if (youtubeUrl) {
        onCallbackSave({ youtubeUrl });
      }

      onCloseModal();
    } finally {
      setIsPublishing(false);
    }
  });

  const galleryModal = isGalleryOpen ? (
    <Suspense fallback={null}>
      <ModalGallery
        modalOpen={isGalleryOpen}
        onCloseModal={onCloseGallery}
        multiple={false}
        isOutsideCampaign={true}
        acceptedFormats={VIDEO_FORMATS}
        pathStorage={`adsCampaigns/${userID}/`}
        modeUpload="sync"
        onCallbackSelectedItems={onSelectVideo}
        onValidateSelectedItems={onValidateGallerySelection}
        closeActionsDisabled={false}
        dialogZIndex={1400}
      />
    </Suspense>
  ) : null;

  return {
    control,
    errors,
    selectedVideo,
    isPublishing,
    onOpenGallery,
    onPublish,
    galleryModal,
    fieldRequiredMessage: t(FIELD_REQUIRED),
  };
};
